import {
  Formik,
  FormikConfig,
  FormikErrors,
  FormikValues,
  yupToFormErrors,
  FormikTouched,
} from 'formik';
import React, { useRef } from 'react';
import { ObjectSchema } from 'yup';
import { pickBy, mapObjIndexed } from 'ramda';
import isPresent from 'utils/isPresent';

interface Config<Values extends FormikValues> extends FormikConfig<Values> {
  validationSchema?: ObjectSchema<Values>;
  validate?: (values: Values) => FormikErrors<Values>; // disallow async validation
}

/**
 * Wrap Formik to make setting initialErrors more convenient. If validation (by
 * either schema or function) is enabled and `initialErrors` is undefined, the
 * validation will be run and pass to Formik any errors as the `initialErrors`
 * prop.
 *
 * This only work if validation can by synchronously.
 */
const FmcForm = <Values extends FormikValues = FormikValues, ExtraProps = {}>(
  props: Config<Values> & ExtraProps,
): JSX.Element => {
  const { initialErrors, initialTouched, validate, validationSchema } = props;

  let { initialValues } = props;

  const errors = useRef(initialErrors);
  const touched = useRef(initialTouched);
  const initialErrorsSet = useRef<boolean>(Boolean(initialErrors));

  if (
    (validate || validationSchema) &&
    errors.current === undefined &&
    !initialErrorsSet.current
  ) {
    if (validate) {
      errors.current = validate(initialValues);
    } else if (validationSchema) {
      try {
        initialValues = {
          ...initialValues,
          ...validationSchema.cast(pickBy(isPresent, initialValues)),
        };
        validationSchema.validateSync(initialValues, { abortEarly: false });
      } catch (e) {
        if (e.name !== 'ValidationError') {
          throw e;
        }
        errors.current = yupToFormErrors(e);
      }
    }
    if (errors.current) {
      // If any initial value is invalid, set it to be touched so that the
      // error messages shows up.
      const hasInvalidInitialValue = mapObjIndexed(
        (value, key) => Boolean(value && initialValues[key]),
        errors.current,
      );
      touched.current = hasInvalidInitialValue as FormikTouched<Values>;
    }
    initialErrorsSet.current = true;
  }

  return (
    <Formik
      {...props}
      initialValues={initialValues}
      initialErrors={errors.current}
      initialTouched={touched.current}
    />
  );
};

export default FmcForm;
